import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CloudOff, Dumbbell, Loader2, Play, RefreshCw, Check, Clock } from 'lucide-react';
import Sheet from './Sheet';
import { formatClock, heroMedia, hasDemoContent } from './playerLogic';

// Peças pequenas do player, partilhadas.

export const Chip = ({ children, tone = 'plain', title, icon: Icon, ...rest }) => (
  <span className={`wp-chip wp-chip-${tone}`} title={title} {...rest}>{Icon ? <Icon size={14} aria-hidden="true" /> : null}{children}</span>
);

/** O progresso do treino: um segmento por exercício (feito / em curso / por fazer). */
export const SegmentedProgress = ({ exercises, index, onJump }) => (
  <div className="wp-segments" role="progressbar" aria-label="Progresso do treino" aria-valuemin={0} aria-valuemax={exercises.length}
    aria-valuenow={exercises.filter((e) => e.status === 'Completed' || e.status === 'Skipped').length}>
    {exercises.map((e, i) => (
      <button key={e.id ?? i} type="button" tabIndex={-1} aria-hidden="true"
        className={`wp-seg ${e.status === 'Completed' ? 'is-done' : e.status === 'Skipped' ? 'is-skipped' : ''} ${i === index ? 'is-current' : ''}`}
        onClick={() => onJump?.(i)} />
    ))}
  </div>
);

/** A faixa dos exercícios do dia: nome e estado, um toque para saltar. O próximo vê-se sem sair do ecrã. */
export const ExerciseStrip = ({ exercises, index, onJump }) => {
  const stripRef = useRef(null);
  const currentRef = useRef(null);
  // O exercício em curso fica centrado na faixa. Rola-se a FAIXA (scrollLeft), não a página: um
  // scrollIntoView aqui arrastava também o conteúdo do exercício e escondia a imagem lá em cima.
  useEffect(() => {
    const strip = stripRef.current;
    const item = currentRef.current;
    if (!strip || !item) return;
    const left = item.offsetLeft - (strip.clientWidth - item.clientWidth) / 2;
    if (typeof strip.scrollTo === 'function') strip.scrollTo({ left, behavior: 'smooth' });
    else strip.scrollLeft = Math.max(0, left);
  }, [index]);
  return (
    <div className="wp-strip" ref={stripRef} role="group" aria-label="Exercícios do treino" data-testid="exercise-strip">
      {exercises.map((e, i) => {
        const state = e.status === 'Completed' ? 'done' : e.status === 'Skipped' ? 'skipped' : i === index ? 'current' : 'pending';
        return (
          <button key={e.id ?? i} type="button" ref={i === index ? currentRef : undefined}
            className={`wp-strip-item is-${state}`} onClick={() => onJump?.(i)}
            aria-label={`${i + 1}. ${e.name}${state === 'done' ? ' (concluído)' : state === 'skipped' ? ' (saltado)' : state === 'current' ? ' (agora)' : ''}`}
            aria-current={i === index ? 'step' : undefined} data-testid={`strip-${i}`}>
            <span className="wp-strip-num" aria-hidden="true">{state === 'done' ? <Check size={13} strokeWidth={3.5} /> : state === 'skipped' ? '–' : i + 1}</span>
            <span className="wp-strip-name">{e.name}</span>
          </button>
        );
      })}
    </div>
  );
};

/** Quantas séries já estão registadas e há quanto tempo o treino começou. Só conta; não guarda nada. */
export const SessionMeta = ({ progress, startedAt }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  const elapsed = startedAt ? Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000)) : 0;
  const logged = progress?.setsLogged ?? 0;
  const target = progress?.setsTarget ?? 0;
  return (
    <span className="wp-session-meta" data-testid="session-meta">
      {target > 0 ? <span data-testid="progress-sets">{logged} de {target} séries</span> : null}
      {startedAt ? <span className="wp-session-clock" data-testid="session-clock">{formatClock(elapsed)}</span> : null}
    </span>
  );
};

export const LoadingState = ({ label = 'A preparar o treino…' }) => (
  <div className="wp-state" role="status" aria-live="polite" aria-busy="true">
    <div className="wp-skeleton wp-skeleton-hero" />
    <div className="wp-skeleton wp-skeleton-line" />
    <div className="wp-skeleton wp-skeleton-line short" />
    <p className="wp-muted wp-center"><Loader2 size={16} className="wp-spin" aria-hidden="true" /> {label}</p>
  </div>
);

export const ErrorState = ({ message, onRetry, onBack }) => (
  <div className="wp-state wp-state-center" role="alert">
    <div className="wp-state-icon wp-bad"><AlertTriangle size={30} aria-hidden="true" /></div>
    <h1 className="wp-h1">Não foi possível carregar</h1>
    <p className="wp-muted">{message || 'Verifique a ligação e tente outra vez.'}</p>
    <div className="wp-actions">
      <button type="button" className="wp-btn wp-btn-primary" onClick={onRetry}><RefreshCw size={18} aria-hidden="true" /> Tentar novamente</button>
      {onBack ? <button type="button" className="wp-btn wp-btn-ghost" onClick={onBack}>Voltar</button> : null}
    </div>
  </div>
);

export const EmptyState = ({ title, message, action }) => (
  <div className="wp-state wp-state-center">
    <div className="wp-state-icon"><Dumbbell size={30} aria-hidden="true" /></div>
    <h1 className="wp-h1">{title}</h1>
    <p className="wp-muted">{message}</p>
    {action}
  </div>
);

/** O estado da sincronização: o atleta sabe sempre se o que fez já está guardado no servidor. */
export const SyncBadge = ({ sync, pending }) => {
  if (pending === 0 && sync === 'idle') return <span className="wp-sync wp-sync-ok" role="status"><Check size={14} aria-hidden="true" /> Guardado</span>;
  if (sync === 'offline') return <span className="wp-sync wp-sync-off" role="status"><CloudOff size={14} aria-hidden="true" /> Sem ligação · {pending} por enviar</span>;
  if (sync === 'retry') return <span className="wp-sync wp-sync-off" role="status"><Clock size={14} aria-hidden="true" /> A tentar de novo · {pending}</span>;
  return <span className="wp-sync wp-sync-busy" role="status"><Loader2 size={14} className="wp-spin" aria-hidden="true" /> A guardar{pending ? ` · ${pending}` : ''}</span>;
};

/** A área grande do exercício: a media que houver, ou um painel com o nome quando ainda não há. Nunca bloqueia nada.
 *  A demonstração está sempre à mão: o botão «Ver como se faz» cobre a largura da imagem, não é um pormenor escondido. */
export const MediaBox = ({ demo, name, groupLabel, onOpenDemo, compact = false }) => {
  const hero = heroMedia(demo);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [hero?.url]);
  const showMedia = hero && !failed;

  return (
    <div className={`wp-media ${compact ? 'is-compact' : ''} ${showMedia ? '' : 'is-empty'}`}>
      {showMedia ? (
        hero.kind === 'video' ? (
          // Um vídeo só mostra o primeiro fotograma; toca no overlay, por escolha do atleta.
          <video className="wp-media-el" src={hero.url} poster={hero.poster || undefined} muted playsInline preload="metadata" aria-label={hero.alt || name} onError={() => setFailed(true)} />
        ) : (
          <img className="wp-media-el" src={hero.url} alt={hero.alt || name} loading="lazy" draggable={false} onError={() => setFailed(true)} />
        )
      ) : (
        <div className="wp-media-fallback" aria-hidden="false">
          <Dumbbell size={compact ? 34 : 54} aria-hidden="true" />
          <span className="wp-media-fallback-name">{groupLabel || 'Exercício'}</span>
          <span className="wp-media-fallback-note">Ainda sem imagem</span>
        </div>
      )}
      <div className="wp-media-shade" aria-hidden="true" />
      {onOpenDemo ? (
        <button type="button" className="wp-demo-btn" onClick={onOpenDemo} data-testid="open-demo"
          aria-label={hasDemoContent(demo) ? 'Ver como se faz' : 'Ver como se faz (ainda sem demonstração)'}>
          <Play size={18} fill="currentColor" aria-hidden="true" /> <span>Ver como se faz</span>
        </button>
      ) : null}
    </div>
  );
};

/**
 * A barra de ações do exercício — o que se faz SEM mexer nas séries: descanso (o cronómetro), histórico e substituir.
 * Fica logo por baixo da imagem: o atleta não tem de a procurar. Um item sem ação real não se mostra (nunca um botão morto).
 */
export const ActionBar = ({ items, label = 'Ações do exercício' }) => {
  const visible = items.filter(Boolean);
  if (visible.length === 0) return null;
  return (
    <div className="wp-action-bar" role="group" aria-label={label} data-testid="exercise-actions">
      {visible.map((item) => {
        const Icon = item.icon;
        return (
          <button key={item.key} type="button" className={`wp-action ${item.tone ? `wp-action-${item.tone}` : ''}`}
            onClick={item.onClick} disabled={item.disabled} data-testid={item.testId} aria-label={item.ariaLabel}>
            <Icon size={20} aria-hidden="true" />
            <span className="wp-action-text">
              <span className="wp-action-label">{item.label}</span>
              {item.sub ? <span className="wp-action-sub">{item.sub}</span> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
};

/** Pede confirmação antes de tudo o que não se desfaz (concluir, abandonar). O botão que acidentalmente se carrega é sempre o seguro. */
export const ConfirmDialog = ({ open, title, children, confirmLabel, cancelLabel = 'Continuar a treinar', danger = false, busy = false, onConfirm, onCancel, reasonLabel }) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  return (
    <Sheet open={open} onClose={busy ? undefined : onCancel} title={title} variant="dialog" dismissible={!busy} testId="confirm-dialog"
      footer={(
        <div className="wp-confirm-actions">
          <button type="button" className="wp-btn wp-btn-primary" onClick={onCancel} disabled={busy} data-autofocus>{cancelLabel}</button>
          <button type="button" className={`wp-btn ${danger ? 'wp-btn-danger' : 'wp-btn-ghost'}`} onClick={() => onConfirm(reason.trim() || null)} disabled={busy} data-testid="confirm-yes">
            {busy ? <Loader2 size={18} className="wp-spin" aria-hidden="true" /> : null} {confirmLabel}
          </button>
        </div>
      )}>
      <div className="wp-confirm-body">
        {children}
        {reasonLabel ? (
          <label className="wp-field-label">
            {reasonLabel}
            <input className="wp-text-input" type="text" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Opcional" />
          </label>
        ) : null}
      </div>
    </Sheet>
  );
};

